// The heredity switch (HER-1, ARCH §28.2). PURE (ENG-1).
//
// Two gamerules, both strings in `World.gamerules` (and so in `WorldMeta.gamerules`,
// which already persists and migrates them): `heredity` ('true' | anything else = off,
// absent = off) and `heredityPace` (1..HER.PACE_MAX, anything else = 1). D21 governs
// Part IX: with the rule off every heredity path is unreachable, so these two readers
// are the ONE place "is it on?" and "how fast?" are decided.
import { HER } from '../config';

/** The gamerule names (§28.2). */
export const HEREDITY_RULE = 'heredity';
export const PACE_RULE = 'heredityPace';

/** Anything that carries gamerules — a `World`, or a test double. */
export interface RuleSource {
  readonly gamerules: ReadonlyMap<string, string>;
}

export type Pace = 1 | 2 | 3 | 4;

const ruleValue = (src: RuleSource | string | undefined, rule: string): string | undefined =>
  typeof src === 'object' ? src.gamerules.get(rule) : src;

/** Is heredity on? Exactly the string `'true'`; absent or junk reads as off. Takes a world
 *  or the rule's raw value. */
export function heredityOn(src: RuleSource | string | undefined): boolean {
  return ruleValue(src, HEREDITY_RULE) === 'true';
}

/** The heredity pace (§28.2): an integer 1..`HER.PACE_MAX`; a non-integer, an
 *  out-of-range number, junk or absence all read as 1. Takes a world or a raw value. */
export function pace(src: RuleSource | string | undefined): Pace {
  const raw = ruleValue(src, PACE_RULE);
  if (raw === undefined || !/^\d+$/.test(raw)) return 1;
  const n = Number(raw);
  return n >= 1 && n <= HER.PACE_MAX ? (n as Pace) : 1;
}

/** `/gamerule`'s return string for the two heredity rules — BINDING copy (§28.2) — or
 *  null for every other rule, which keeps `Gamerule <name> set to <value>`. The line
 *  reports the EFFECTIVE value, so `/gamerule heredityPace 9` truthfully says ×1. */
export function heredityRuleMessage(rule: string, value: string): string | null {
  if (rule === HEREDITY_RULE) return `Heredity is now ${heredityOn(value) ? 'on' : 'off'} in this world.`;
  if (rule === PACE_RULE) return `Heredity pace is now ×${pace(value)}.`;
  return null;
}
